'use client';

import { fetchGetPushMsg, memberAppToken } from '@module/apis/client-side/push';
import SettingConfig from '@module/config/setting';
import SafeLocalStorage from '@module/lib/storage/safe-local-storage';
import { deviceIdSafeLocalStorage } from '@module/lib/storage/storageImplementations';
import { deleteCookie, getCookie } from 'cookies-next';
import { deleteToken, getMessaging, getToken, type Messaging, onMessage, type Unsubscribe } from 'firebase/messaging';
import md5 from 'md5';
import TimeConstants from '../utils/item';
import { app, firebaseConfig, VAPID_PUBLIC_KEY } from './initFirebase';

interface TokenParam {
  token?: string;
  deviceId: string;
  expireDeviceId?: string;
}

const registerServiceWorker = async () => {
  return navigator.serviceWorker.register(`/firebase-messaging-sw.js?js=${SettingConfig.CURRENT_SITE}`);
};

export class PushNotification {
  private messaging: Messaging | null = null;
  private swReg: ServiceWorkerRegistration | null = null;
  private fcmToken = '';

  private stopMsg: null | Unsubscribe = null;
  constructor() {
    try {
      if (!app || !firebaseConfig) return;
      this.messaging = getMessaging(app);
    } catch (_error) {
      this.messaging = null;
    } finally {
      if (this.messaging === null) {
        console.error('Failed to initialize Firebase Messaging');
      }
    }
  }

  public check() {
    if (!SafeLocalStorage.isStorageAvailable()) return false;
    return (
      typeof window !== 'undefined' &&
      'serviceWorker' in navigator &&
      'Notification' in window &&
      location.protocol === 'https:' &&
      !!this.messaging &&
      typeof Notification !== 'undefined'
    );
  }

  public message() {
    return this.messaging;
  }

  public async init() {
    if (!this.check()) return false;
    if (Notification.permission === 'denied') return false;
    if (Notification.permission === 'granted') return true;
    if (Notification.permission === 'default') {
      const result = await Notification.requestPermission();
      if (result !== 'granted') return false;

      const pushMsg = await fetchGetPushMsg();
      if (pushMsg.result) {
        const { webNotiConfirm } = pushMsg.pushMsg;
        const icon = this.getNotificationIcon();
        // eslint-disable-next-line no-new
        new Notification(webNotiConfirm.title, {
          icon,
          body: webNotiConfirm.text,
        });
      }
      return true;
    }
    return false;
  }

  private getNotificationIcon() {
    return `/img/${SettingConfig.CURRENT_SITE}-apple-icon.png`;
  }

  public setCookie(token: string) {
    const tokenId = this.getDeviceId(token);
    document.cookie = `webDeviceId=${tokenId}; domain=.${SettingConfig.CURRENT_SITE_INFO.host}; max-age=${TimeConstants.ONE_DAY * 30}; secure; sameSite=strict; path=/`;
  }

  public async register() {
    console.info('register push notification');
    if (!this.check()) return;
    if (this.swReg) return;
    if (this.stopMsg) this.stopMsg();

    console.info('register fore -> back');
    this.stopMsg = this.onMessageHandler();

    try {
      const existingSW = await navigator.serviceWorker.getRegistration();
      if (existingSW) {
        this.swReg = existingSW;
        if (this.messaging && !this.fcmToken) {
          this.fcmToken = await getToken(this.messaging, {
            vapidKey: VAPID_PUBLIC_KEY as string,
            serviceWorkerRegistration: existingSW,
          });
          if (this.fcmToken) {
            const updateFCMTokenRes = await this.sendToken(this.fcmToken);
            if (updateFCMTokenRes.result) {
              this.setCookie(this.fcmToken);
            }
          }
        }
      } else {
        this.swReg = await registerServiceWorker();

        await navigator.serviceWorker.ready.then(async (registration) => {
          if (!this.messaging) return;

          this.fcmToken = await getToken(this.messaging, {
            vapidKey: VAPID_PUBLIC_KEY as string,
            serviceWorkerRegistration: registration,
          });
          if (this.fcmToken) {
            const updateFCMTokenRes = await this.sendToken(this.fcmToken);
            if (updateFCMTokenRes.result) {
              this.setCookie(this.fcmToken);
            }
          }
        });
      }
    } catch (error) {
      if (error instanceof Error) {
        console.warn('Failed to register push notification', error);
        this.unregister();
      }
    }
  }

  public async unregister() {
    console.info('unregister push notification');
    try {
      if (!this.swReg) {
        try {
          this.swReg = await registerServiceWorker();
          if (this.messaging && !this.fcmToken) {
            this.fcmToken = await getToken(this.messaging, {
              vapidKey: VAPID_PUBLIC_KEY as string,
              serviceWorkerRegistration: this.swReg,
            });
          }
        } catch (err) {
          console.error('Failed to register service worker:', err);
        }
      }

      // 토큰 삭제 (Messaging 객체 사용)
      if (this.messaging) await deleteToken(this.messaging);

      // Service Worker 등록 해제
      if (this.swReg) {
        await this.swReg.unregister();
        this.swReg = null;
      }

      // 디바이스 ID 저장소 초기화
      deviceIdSafeLocalStorage.remove();
      console.info('Push notification device ID removed from local storage');
      // 쿠키 삭제

      deleteCookie('webDeviceId');
      console.info('Push notification cookie deleted');
      if (this.fcmToken) {
        await this.delToken(this.fcmToken);
        this.fcmToken = '';
      }

      // 추가 검증: 모든 Service Worker 등록 확인
      const regs = await navigator.serviceWorker.getRegistrations();

      for (const reg of regs) {
        if (reg.active?.scriptURL.includes('firebase') || reg.scope.includes('firebase')) {
          await reg.unregister();
        }
      }
    } catch (error) {
      console.error('Error during unregister:', error);
    }
  }

  private getDeviceId(token: string) {
    return `web:${md5(token)}`;
  }

  public destroy() {
    if (this.stopMsg) this.stopMsg();
  }

  private async sendToken(token: string) {
    const prevDeviceId = getCookie('webDeviceId');
    const tokenId = this.getDeviceId(token);
    const sendToken: TokenParam = { token, deviceId: tokenId };
    if (prevDeviceId && prevDeviceId !== tokenId) sendToken.expireDeviceId = prevDeviceId;
    return memberAppToken(sendToken);
  }

  static async expireRemainingToken() {
    deviceIdSafeLocalStorage.remove();
    const token = getCookie('webDeviceId');
    if (token) {
      return memberAppToken({ expireDeviceId: token });
    }

    return false;
  }

  /** 포그라운드 푸시 */
  private onMessageHandler() {
    if (!this.messaging) {
      console.error('Messaging is not initialized');
      return () => {};
    }
    return onMessage(this.messaging, (payload) => {
      if (navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage(JSON.stringify({ type: 'push', payload }));
      } else {
        console.error('Service worker controller not found');
      }
    });
  }

  public async pushReqEvent(event: 'register' | 'unregister') {
    if (event === 'register') {
      const isInit = await this.init();

      if (!isInit) {
        return;
      }
      return this.register();
    }
    if (event === 'unregister') {
      this.unregister();
    }
  }

  private async delToken(token: string) {
    const tokenId = this.getDeviceId(token);
    await memberAppToken({
      expireDeviceId: tokenId,
    });
  }
}
